import React, { memo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import {
  Key, Laptop, Globe, Layers, Database, Users, Settings, Shield, ShieldCheck, ShieldAlert,
  CheckCircle, Activity, Package, Lock, UserCheck, Server, GitBranch, Grid, EyeOff, Tag,
  Search, Archive, Box, LogIn, Code, FileText, Smartphone, Edit2
} from 'lucide-react';

/**
 * Icon mapping for node types
 */
const ICON_MAP = {
  key: Key,
  laptop: Laptop,
  globe: Globe,
  layers: Layers,
  database: Database,
  users: Users,
  settings: Settings,
  shield: Shield,
  'shield-check': ShieldCheck,
  'shield-alert': ShieldAlert,
  'check-circle': CheckCircle,
  activity: Activity,
  package: Package,
  lock: Lock,
  'user-check': UserCheck,
  server: Server,
  'git-branch': GitBranch,
  grid: Grid,
  'eye-off': EyeOff,
  tag: Tag,
  search: Search,
  archive: Archive,
  box: Box,
  'log-in': LogIn,
  code: Code,
  'file-text': FileText,
  smartphone: Smartphone
};

/**
 * Status colors for adoption status badge
 */
const STATUS_COLORS = {
  IN_USE: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', label: 'In Use' },
  PLANNED: { bg: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', label: 'Planned' },
  EVALUATING: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', label: 'Evaluating' },
  NOT_APPLICABLE: { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280', label: 'N/A' }
};

/**
 * Node dimensions - width is fixed, height is auto (min-height in CSS)
 */
const NODE_WIDTH = 160;
const NODE_MIN_HEIGHT = 90;
const INPUT_NODE_WIDTH = 110;
const INPUT_NODE_MIN_HEIGHT = 65;

/**
 * ArchitectureNode Component
 *
 * Renders a draggable technology node in the architecture diagram.
 * Supports input nodes (Users, Devices), tech nodes, and output nodes (Applications).
 * When multiple vendors cover a category, shows split-color borders.
 */
const ArchitectureNode = memo(function ArchitectureNode({
  node,
  isSelected,
  onSelect,
  onDelete,
  onOpenSelector,
  readOnly = false,
  // Vendor overlay props
  isHighlighted = false,
  vendorCoverage = [], // Array of { vendorName, product, displayColor, logoUrl }
  isDimmed = false,
  // Edge creation props
  onAnchorMouseDown,
  onAnchorMouseEnter,
  onAnchorMouseLeave,
  isEdgeCreating = false,
  isValidTarget = false
}) {
  const {
    id,
    label,
    vendorName,
    icon,
    x,
    y,
    adoptionStatus,
    isInput,
    isOutput,
    locked
  } = node;

  const isDraggable = !locked && !readOnly;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging
  } = useDraggable({
    id,
    disabled: !isDraggable,
    data: { type: 'node', node }
  });

  // Calculate position with drag transform
  const style = {
    position: 'absolute',
    left: x,
    top: y,
    transform: transform
      ? `translate(${transform.x}px, ${transform.y}px)`
      : undefined,
    zIndex: isDragging ? 1000 : isSelected ? 100 : (isHighlighted ? 50 : 1),
    cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
    opacity: isDragging ? 0.8 : 1,
    touchAction: 'none'
  };

  // Get icon component
  const IconComponent = ICON_MAP[icon] || Layers;

  // Determine node style based on type
  const isSpecialNode = isInput || isOutput;
  const nodeWidth = isSpecialNode ? INPUT_NODE_WIDTH : NODE_WIDTH;
  const nodeMinHeight = isSpecialNode ? INPUT_NODE_MIN_HEIGHT : NODE_MIN_HEIGHT;

  // Get status colors if applicable
  const statusInfo = adoptionStatus ? STATUS_COLORS[adoptionStatus] : null;

  // Determine if we have multiple vendors (for split border)
  const hasMultipleVendors = vendorCoverage.length > 1;
  const primaryColor = vendorCoverage[0]?.displayColor || null;
  const secondaryColor = vendorCoverage[1]?.displayColor || null;

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect?.(id);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    // Only open selector for tech nodes (not input/output nodes)
    if (!isSpecialNode && !readOnly && onOpenSelector) {
      onOpenSelector(node);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(id);
  };

  // Build dynamic class list
  const nodeClasses = [
    'arch-node',
    isSelected && 'arch-node--selected',
    isInput && 'arch-node--input',
    isOutput && 'arch-node--output',
    isDragging && 'arch-node--dragging',
    !isSpecialNode && !readOnly && 'arch-node--clickable',
    isHighlighted && 'arch-node--highlighted',
    hasMultipleVendors && 'arch-node--multi-vendor',
    isDimmed && 'arch-node--dimmed'
  ].filter(Boolean).join(' ');

  // Build container styles including highlight colors
  const containerStyle = {
    width: nodeWidth,
    minHeight: nodeMinHeight,
    ...(isHighlighted && primaryColor ? {
      '--highlight-color': primaryColor,
      '--highlight-color-1': primaryColor,
      '--highlight-color-2': secondaryColor || primaryColor
    } : {})
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={nodeClasses}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      {...(isDraggable ? { ...attributes, ...listeners } : {})}
    >
      {/* Node container */}
      <div
        className="arch-node__container"
        style={containerStyle}
      >
        {/* Split border overlay for multi-vendor */}
        {isHighlighted && hasMultipleVendors && (
          <div className="arch-node__split-border">
            <div
              className="arch-node__split-border-left"
              style={{ background: primaryColor }}
            />
            <div
              className="arch-node__split-border-right"
              style={{ background: secondaryColor }}
            />
          </div>
        )}

        {/* Icon */}
        <div className={`arch-node__icon ${isSpecialNode ? 'arch-node__icon--special' : ''}`}>
          <IconComponent size={isSpecialNode ? 20 : 24} />
        </div>

        {/* Label */}
        <div className="arch-node__label">{label}</div>

        {/* Vendor name (for tech nodes) */}
        {vendorName && !isSpecialNode && (
          <div className="arch-node__vendor">{vendorName}</div>
        )}

        {/* Adoption status badge */}
        {statusInfo && (
          <div
            className="arch-node__status"
            style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
          >
            {statusInfo.label}
          </div>
        )}

        {/* Vendor logo badges (when highlighted) - show up to 2 logos */}
        {isHighlighted && vendorCoverage.length > 0 && (
          <div className="arch-node__vendor-logos">
            {vendorCoverage.slice(0, 2).map((v, idx) => v.logoUrl && (
              <img
                key={v.vendorName}
                src={v.logoUrl}
                alt={v.vendorName}
                className="arch-node__vendor-logo"
                style={{
                  borderColor: v.displayColor,
                  right: idx === 0 ? '-10px' : '16px',
                  zIndex: 10 - idx
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ))}
          </div>
        )}

        {/* Vendor product overlay - shows products from all selected vendors */}
        {isHighlighted && vendorCoverage.length > 0 && (
          <div className={`arch-node__vendor-overlay ${hasMultipleVendors ? 'arch-node__vendor-overlay--multi' : ''}`}>
            {vendorCoverage.map((v, idx) => (
              <span
                key={v.vendorName}
                className="arch-node__vendor-overlay-product"
                style={{ backgroundColor: v.displayColor }}
              >
                {v.product}
              </span>
            ))}
          </div>
        )}

        {/* Edit hint icon (for clickable tech nodes) */}
        {!isSpecialNode && !readOnly && !isHighlighted && (
          <div className="arch-node__edit-hint">
            <Edit2 size={12} />
          </div>
        )}

        {/* Delete button (when selected and not locked/readonly) */}
        {isSelected && !locked && !readOnly && !isSpecialNode && (
          <button
            className="arch-node__delete"
            onClick={handleDelete}
            title="Remove node"
          >
            &times;
          </button>
        )}
      </div>

      {/* Connection points (anchor dots) - interactive for edge creation */}
      {!isInput && (
        <div
          className={`arch-node__anchor arch-node__anchor--left ${isValidTarget ? 'arch-node__anchor--valid-target' : ''} ${isEdgeCreating ? 'arch-node__anchor--creating' : ''}`}
          onMouseDown={(e) => {
            if (readOnly) return;
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            onAnchorMouseDown?.(id, 'left', { x: node.x, y: node.y + nodeMinHeight / 2 });
          }}
          onMouseEnter={() => {
            if (isEdgeCreating) onAnchorMouseEnter?.(id, 'left');
          }}
          onMouseLeave={() => {
            if (isEdgeCreating) onAnchorMouseLeave?.();
          }}
          onMouseUp={(e) => {
            if (isEdgeCreating && isValidTarget) {
              e.stopPropagation();
              // Edge completion is handled by the canvas
            }
          }}
        />
      )}
      {!isOutput && (
        <div
          className={`arch-node__anchor arch-node__anchor--right ${isValidTarget ? 'arch-node__anchor--valid-target' : ''} ${isEdgeCreating ? 'arch-node__anchor--creating' : ''}`}
          onMouseDown={(e) => {
            if (readOnly) return;
            e.stopPropagation();
            onAnchorMouseDown?.(id, 'right', { x: node.x + nodeWidth, y: node.y + nodeMinHeight / 2 });
          }}
          onMouseEnter={() => {
            if (isEdgeCreating) onAnchorMouseEnter?.(id, 'right');
          }}
          onMouseLeave={() => {
            if (isEdgeCreating) onAnchorMouseLeave?.();
          }}
          onMouseUp={(e) => {
            if (isEdgeCreating && isValidTarget) {
              e.stopPropagation();
              // Edge completion is handled by the canvas
            }
          }}
        />
      )}
    </div>
  );
});

/**
 * Export node dimensions for edge calculations
 */
export const getNodeDimensions = (node) => {
  const isSpecialNode = node.isInput || node.isOutput;
  return {
    width: isSpecialNode ? INPUT_NODE_WIDTH : NODE_WIDTH,
    height: isSpecialNode ? INPUT_NODE_MIN_HEIGHT : NODE_MIN_HEIGHT
  };
};

/**
 * Get anchor point for edge connections
 */
export const getNodeAnchor = (node, side) => {
  const dims = getNodeDimensions(node);
  const centerY = node.y + dims.height / 2;

  if (side === 'left') {
    return { x: node.x, y: centerY };
  }
  return { x: node.x + dims.width, y: centerY };
};

export default ArchitectureNode;
